<script lang="ts" setup>
import BlockContentBand from '@/vue/organisms/home/block-content-band.vue';
import ReccoFeaturedRecommendation from '@/vue/organisms/recco/recco-featured-recommendation.vue';
import ExploreFilterBar from '@/vue/organisms/recco/explore-filter-bar.vue';
import ExploreLibraryRail from '@/vue/organisms/recco/explore-library-rail.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {ref, type Ref, toRaw, computed, watch, onActivated, onDeactivated, onMounted, onBeforeMount, onBeforeUnmount} from 'vue';
import {debounce} from 'quasar';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import {useApi} from '@/ts/composables/stateful/use-api';
import {EXPLORE_LIBRARY_MAX_SIZE} from '@/ts/api/modules/content-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {ReccoTopicDTO, type ReccoTopicResponse} from '@/ts/types/dto/recco/topic.dto';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {useExploreFilters} from '@/ts/composables/pure/use-explore-filters';
import {useExploreLibraryGrouping} from '@/ts/composables/pure/use-explore-library-grouping';
import {topicLabelKey} from '@/ts/utils/recco-label-keys';
import {orderStartedFirst} from '@/ts/utils/explore-catalog-order';
import {LibraryContentTypeDTO, type ExploreLibraryItemDTO, type ExploreLibraryPartnerDTO} from '@/ts/types/dto/recco/library.dto';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {useRoute, useRouter, type RouteLocationRaw} from 'vue-router';
import {EXPLORE_SAVED_QUERY, pagesPath} from '@/ts/router/pages-path';
import {useI18n} from 'vue-i18n';
import {MetricCategory, MetricAction} from '@/ts/types/dto/metric-dto';
import {wasPopNavigation} from '@/ts/utils/history-navigation';
import {storeToRefs} from 'pinia';
import {useBookmarksStore} from '@/ts/store/bookmarks-store';
import {hasSameContent} from '@/ts/utils/pure-functions';

// Kept alive by main-base-page so filters, rails and scroll survive back navigation.
defineOptions({name: 'ExplorePage'});

const logger = useLogger('ExplorePage');
const api = useApi();
const router = useRouter();
const route = useRoute();
const {canAccessExplore} = useFeatureAccess();
const {t: $t} = useI18n();

/**
 * Writes fetched data only when it differs from what the page holds. Every response is new objects,
 * so assigning it as is re-renders every band and rail it feeds even when nothing changed, which is
 * what the refresh on each return to the page usually brings back.
 */
function assignIfChanged<T>(target: Ref<T>, next: T): void {
  // Read raw: serialising through the reactive proxy costs several times more on a long list.
  if (!hasSameContent(toRaw(target.value), next)) {
    target.value = next;
  }
}

// State variables
const allRecommendations = ref<ComponentContent[]>([]);
const allTopics = ref<ReccoTopicResponse>([]);

const loading = ref<boolean>(true);
const error = ref<string | undefined>('');
/** Highest-ranked recommendation, the fallback for the large spot when no piece is flagged. */
const topRankedRecommendation = ref<ComponentContent | null>(null);

// avoids that the assessment get rendered before top components (avoids page jump)
const areTopElementsReady = ref<boolean>(false);

const exploreFilters = useExploreFilters();
const {
  mediaTypes,
  contentTypes,
  topics: selectedTopics,
  saved,
  partnerIds,
  isActive: filtersActive,
  isSavedOnly,
  activeCount,
  toggleMediaType,
  toggleContentType,
  toggleTopic,
  toggleSaved,
  togglePartner,
  clearAll,
} = exploreFilters;

// Partner chips — one per pack available to the org; the bar hides them when empty.
const partners = ref<ExploreLibraryPartnerDTO[]>([]);
// The organisation's whole partner pack, read by partnerIds alone (IPD-3079). Its own request:
// LibraryItem does not carry the pack it came from, so a catalogue response cannot be filtered
// down to "pack content" on the client.
const partnerPackItems = ref<ExploreLibraryItemDTO[]>([]);
// Whether the pack request has settled. The large spot waits for it: deciding without it shows
// the recommendation first and then swaps it for the pack's flagged piece under the reader.
const partnerPackSettled = ref<boolean>(false);
let partnerPackRequestId = 0;
const availableTopics = computed<ReccoTopicDTO[]>(() => allTopics.value);

// Rails follow the topic facet when it is active. Without this, filtering by Relaxation still
// renders a Mental Health rail — an item carries several topics, so it matches a rail for a
// topic the user just filtered out, and the page reads as if the filter had not worked.
// Nothing is dropped: with the facet active the BE only returns items carrying a selected
// topic, so every educational item still lands in at least one rail.
const railTopics = computed<ReccoTopicDTO[]>(() => (selectedTopics.value.length > 0
  ? availableTopics.value.filter((topic) => selectedTopics.value.includes(topic))
  : availableTopics.value));

// Filtered (library) results — populated only while at least one facet is active.
const libraryItems = ref<ExploreLibraryItemDTO[]>([]);
const libraryLoading = ref<boolean>(false);
// A failed fetch must read as an error, not as "no content matches these filters".
const libraryError = ref<boolean>(false);
// The facets' full match count, straight from the backend — not items.length, which the page
// size bounds. Drives the "N results" label under the filter band.
const libraryTotal = ref<number>(0);

// The filter band is collapsed behind the "Filters" toggle.
const filtersOpen = ref<boolean>(false);

// Re-group the flat library into the section structure (§2: filter within sections,
// hide empty, no flat grid). Sections are not exclusive — see the composable.
const {featuredItems, recommendedItems, challengeItems, topicRails, otherEducationalItems, hasResults: hasFilteredResults}
  = useExploreLibraryGrouping(libraryItems, railTopics);

// Pluralised result count shown under the filter band when filters are active (mockup: "N results").
// The backend reports the match count before its page window, so this is the real total and no
// longer has to read as a floor ("N+") when the set was cut.
const resultsLabel = computed<string>(() => {
  const count = libraryTotal.value;
  return $t('explore_n_results', count, {named: {count}});
});

// Explore never shows programs (medical licensing plumbing today). Asking the backend to exclude
// them keeps totalElements in step with what the sections actually render; filtering only on the
// client would count rows the page then hides.
const RENDERABLE_CONTENT_TYPES = [LibraryContentTypeDTO.Challenge, LibraryContentTypeDTO.Educational];

const contentTypesWithoutPrograms = (): LibraryContentTypeDTO[] => {
  const selected = exploreFilters.filters.value.contentTypes;
  const renderable = selected.filter((type) => type !== LibraryContentTypeDTO.Program);
  // Two different empties: nothing selected, and a selection of nothing but Program. Both have to
  // fall through to the allow-list — an empty array omits the param entirely, which asks for the
  // whole library, so a Program-only selection would report a total covering everything while the
  // sections rendered nothing. The filter bar hides Program today, so this is a guard, not a fix.
  return renderable.length > 0 ? renderable : RENDERABLE_CONTENT_TYPES;
};

/**
 * The piece in the large spot, and why it is there.
 *
 * A flagged piece in the organisation's pack wins. Failing that, a flagged recommendation — the
 * ticket is explicit that featured content need not be partner content. Failing both, the
 * top-ranked recommendation, so the slot is never empty. The three are different reasons, so the
 * eyebrow says which, and whatever takes the spot is held out of the sections below.
 */
const largeSpot = computed<{content: ComponentContent; eyebrowKey: string} | null>(() => {
  const flaggedInPack = partnerPackItems.value.find((item) => item.content?.isFeatured)?.content;
  if (flaggedInPack) {
    return {content: flaggedInPack, eyebrowKey: 'explore_featured_eyebrow'};
  }
  const flaggedRecommendation = allRecommendations.value.find((item) => item.isFeatured);
  if (flaggedRecommendation) {
    return {content: flaggedRecommendation, eyebrowKey: 'explore_featured_eyebrow'};
  }
  return topRankedRecommendation.value
    ? {content: topRankedRecommendation.value, eyebrowKey: 'recco_for_you'}
    : null;
});

/**
 * The pack minus whatever is in the large spot. Compared on the row key rather than `content.id`:
 * a challenge has no `content`, so an id comparison drops every challenge in the pack whenever the
 * large spot is empty (undefined === undefined).
 */
const partnerBandItems = computed<ExploreLibraryItemDTO[]>(() => {
  const spotId = largeSpot.value?.content.id ?? null;
  const rest = spotId === null
    ? partnerPackItems.value
    : partnerPackItems.value.filter((item) => item.content?.id !== spotId);
  // The pill is stripped so the partner badge reads alone, the rule IPD-2942 set for this band.
  // The rows are genuinely engine-recommended, but carrying both marks makes the card noisy.
  return rest.map((item) => ({...item, recommended: false}));
});

/**
 * Recommendations minus the large spot. Featured ones are kept: they used to have a band of their
 * own, and now that the band is the partner pack, dropping them would leave a flagged
 * recommendation outside a pack with nowhere on the page to appear.
 */
const forYouRecommendations = computed<ComponentContent[]>(() =>
  allRecommendations.value.filter((item) => item.id !== largeSpot.value?.content.id));


// Guards against out-of-order responses: only the latest request may write the results.
let libraryRequestId = 0;
// Whether the pending (debounced) request is a background sync — see requestLibrary.
let pendingSilent = true;

// `silent` = the user did not ask for this request, so a failure keeps what is on screen
// instead of swapping it for the error card (same idea as fetchRecommendations(silent)).
const fetchLibrary = async(silent = false): Promise<void> => {
  const requestId = ++libraryRequestId;
  if (!silent) {
    libraryLoading.value = true;
    libraryError.value = false;
  }
  try {
    // The whole result set, not the endpoint's default: the sections render every row they are
    // handed, so anything the page window cut would be missing from them.
    const {items, totalElements} = await api.content.getExploreLibrary(
      // Programs are dropped below, so they must not be requested either — otherwise they would
      // count towards totalElements and the label would exceed what the sections render.
      {...exploreFilters.filters.value, contentTypes: contentTypesWithoutPrograms()},
      EXPLORE_LIBRARY_MAX_SIZE,
    );
    if (requestId === libraryRequestId) {
      // Programs must never surface in Explore (medical licensing plumbing today) —
      // drop them at the source so the sections AND the results count agree.
      assignIfChanged(libraryItems, items.filter((item) => item.contentType !== LibraryContentTypeDTO.Program));
      libraryTotal.value = totalElements;
    }
  } catch (err) {
    if (requestId === libraryRequestId && !silent) {
      libraryItems.value = [];
      libraryTotal.value = 0;
      libraryError.value = true;
    }
    logger.error('Failed to fetch the filtered library', {err})();
  } finally {
    // Not gated on `silent`: a silent request that overtakes a user-initiated one inherits the
    // duty to clear its spinner.
    if (requestId === libraryRequestId) {
      libraryLoading.value = false;
    }
  }
};

// Coalesce rapid facet toggles into a single request (each toggle would otherwise
// fire its own /explore/library call); the requestId guard still handles ordering.
const debouncedFetchLibrary = debounce(() => {
  const silent = pendingSilent;
  pendingSilent = true;
  void fetchLibrary(silent);
}, 250);

// Silent only while every request folded into the same debounce window was silent.
const requestLibrary = (silent: boolean): void => {
  pendingSilent &&= silent;
  debouncedFetchLibrary();
};

// Retry from the error state (the click handler must be void, not a floating promise).
const retryLibrary = (): void => {
  void fetchLibrary();
};

// Re-query the library whenever the facet selection changes; clear it when no facet is active.
watch(
  () => exploreFilters.filters.value,
  () => {
    if (filtersActive.value) {
      // Show the loading state immediately so the debounce window doesn't flash the
      // empty state before the request starts (stale results stay put during a re-fetch).
      libraryLoading.value = true;
      requestLibrary(false);
    } else {
      // Invalidate any in-flight/pending request so a late response can't repopulate the cleared view.
      debouncedFetchLibrary.cancel();
      pendingSilent = true;
      libraryRequestId++;
      libraryLoading.value = false;
      libraryError.value = false;
      libraryItems.value = [];
      libraryTotal.value = 0;
    }
  },
  {deep: true},
);

// Refetching reassigns the rails, which moves the content under a just-restored scroll
// position — so a RETURN (back/forward) only refreshes data this old. A fresh entry
// (nav bar, link) resets the scroll anyway, and always refreshes.
const REFRESH_STALE_AFTER_MS = 5 * 60 * 1000;
let lastLoadedAt = 0;

// The Saved results are server-driven, so a bookmark toggled anywhere has to re-query.
// Watch the toggle COUNT, not the list: nothing hydrates the store on this page, so un-saving
// an item it never held changes neither the list's length nor its ids.
// Ignores REFRESH_STALE_AFTER_MS on purpose — a Saved list still showing a removed item is
// worse than a shifted scroll.
const {mutationCount: bookmarkToggles} = storeToRefs(useBookmarksStore());
watch(bookmarkToggles, () => {
  if (saved.value) {
    requestLibrary(true);
  }
});



// Default-view catalog sections: everything below "For you" is CATALOG, not
// recommendations — topic rails (educational grouped by topic) and Challenges
// (including the user's started challenges with their progress, per the mockup).
// No Programs section: today's program data is medical licensing plumbing, and the
// user-facing (non-medical) Programs launch in September with a model still TBD.
// Each rail hides entirely when empty or on error.
const challengeCatalogItems = ref<ExploreLibraryItemDTO[]>([]);
const topicCatalogRails = ref<{topic: ReccoTopicDTO; items: ExploreLibraryItemDTO[]}[]>([]);

/**
 * The Challenges section as a destination (IPD-3103). The Today page advertises challenges and had
 * nowhere narrower to send anyone than the top of this page, which left them to find the section
 * themselves.
 *
 * A hash rather than a query or a route of its own: it names a place on this page, which is what it
 * is, and it needs nothing of whoever links to it. Anyone arriving this way has the section — the
 * promo that sends them is only offered to a reader with challenges to start — and a hash that
 * matches nothing leaves the page where it opened, so no case needs a branch of its own.
 */
const CHALLENGES_ANCHOR = 'explore-challenges';

/**
 * The rails' "View all" targets, built once. Written as literals in the template they are new
 * objects on every render of the page, and every rail they are passed to renders again with it.
 */
const PARTNER_CONTENT_LINK: RouteLocationRaw = {name: 'partner-content'};
const CHALLENGES_LINK: RouteLocationRaw = {name: 'topic', params: {topic: 'challenges'}};
const TOPIC_LINKS = new Map<ReccoTopicDTO, RouteLocationRaw>(Object.values(ReccoTopicDTO)
  .map((topic) => [topic, {name: 'topic', params: {topic}}]));

// A topic the backend serves before the enum knows it still gets its link.
function topicLink(topic: ReccoTopicDTO): RouteLocationRaw {
  return TOPIC_LINKS.get(topic) ?? {name: 'topic', params: {topic}};
}
const ANCHOR_POLL_MS = 100;
const ANCHOR_TIMEOUT_MS = 10000;
// How long the position is re-taken after the first scroll: long enough for the partner band's two
// serial round trips, short enough not to follow the page around once it has settled.
const ANCHOR_SETTLE_MS = 3000;

// Bumped when the page goes away and by each new wait, so one cannot outlive the visit that asked
// for it, nor two run at once when the reader arrives twice.
let anchorWaitGeneration = 0;

const nextAnchorPoll = async(): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ANCHOR_POLL_MS);
  });

/**
 * Waits for the section to exist. Null when it never appears, or when the page went away or a later
 * arrival cancelled this wait.
 *
 * The browser's own hash handling has already given up by then: the section is drawn from a call of
 * its own, so on arrival there is nothing at the anchor. Waiting on the element rather than on the
 * two pieces of state behind it — the readiness flag and the list — because those say when the data
 * arrived, not when the row was painted, and the scroll needs the row.
 */
async function waitForAnchor(generation: number): Promise<HTMLElement | null> {
  const deadline = Date.now() + ANCHOR_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (generation !== anchorWaitGeneration) {
      return null;
    }
    const section = document.getElementById(CHALLENGES_ANCHOR);
    if (section) {
      return section;
    }
    // eslint-disable-next-line no-await-in-loop
    await nextAnchorPoll();
  }
  return null;
}

// Smooth is an animation, and the option animates whether or not the reader asked for motion — the
// browser does not consult the preference for it, so this does.
function scrollToAnchor(section: HTMLElement): void {
  section.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'start',
  });
}

/**
 * Holds the rail in frame while the page above it is still growing.
 *
 * Three sections above this one arrive from calls of their own: the partner band is two serial
 * round trips away, the "For you" band waits on the recommended challenges, and the hero's skeleton
 * collapses to nothing for a reader with no piece for the spot. One of them landing once the scroll
 * has finished costs nothing — the browser's own scroll anchoring absorbs content inserted above
 * the viewport. One landing *during* the smooth scroll does not: the animation runs to an offset
 * worked out when it started, so it finishes the height of the new section short of the rail.
 *
 * Read from `offsetTop`, which moves only when the content above the rail does — the viewport box
 * also moves under the scroll itself. Dropped on the reader's first input, none of which a
 * programmatic scroll raises: from that moment the page is theirs. A press counts — Quasar's menus
 * do not follow an ancestor scroll, so moving the page under one the reader just opened closes it.
 */
async function holdAtAnchor(section: HTMLElement, generation: number): Promise<void> {
  let takenOver = false;
  const release = (): void => {
    takenOver = true;
  };
  const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
  inputs.forEach((name) => { window.addEventListener(name, release, {passive: true}); });

  try {
    let offset = section.offsetTop;
    const deadline = Date.now() + ANCHOR_SETTLE_MS;
    while (Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await nextAnchorPoll();
      if (takenOver || generation !== anchorWaitGeneration) {
        return;
      }
      if (section.offsetTop !== offset) {
        offset = section.offsetTop;
        scrollToAnchor(section);
      }
    }
  } finally {
    inputs.forEach((name) => { window.removeEventListener(name, release); });
  }
}

/**
 * Opens the page at the Challenges section when the link asked for it.
 *
 * Clears the filters as well as scrolling: they live on the instance and survive the trip to Today
 * and back (IPD-2942), and a filtered page draws its own grouping instead of the catalogue rails —
 * so the section this link names is not on screen to scroll to, and leaving the filters up would
 * land the reader at the top of a page that may not show a challenge at all. An arrival from
 * outside carries a destination with it, which outranks a selection left behind on a previous
 * visit.
 */
async function openAtRequestedAnchor(): Promise<void> {
  if (route.hash !== `#${CHALLENGES_ANCHOR}`) {
    return;
  }

  clearAll();
  // The band's own open state survives the trip too, and left up over emptied facets it reads as
  // every chip having been silently deselected.
  filtersOpen.value = false;

  const generation = ++anchorWaitGeneration;
  const section = await waitForAnchor(generation);
  if (!section) {
    return;
  }
  scrollToAnchor(section);
  await holdAtAnchor(section, generation);
}

/**
 * Opens the page on the reader's bookmarks when the link asked for them (IPD-3328).
 *
 * Clears the rest of the selection for the same reason the Challenges anchor does: it survives the
 * trip away and back, and an arrival from outside carries a destination with it, which outranks a
 * selection left behind on a previous visit. "Saved" alone is also the one selection whose empty
 * state speaks about bookmarks.
 */
function openOnRequestedSaved(): void {
  if (route.query.saved !== EXPLORE_SAVED_QUERY.saved) {
    return;
  }
  clearAll();
  saved.value = true;
}

// Recommended challenges mixed into the "For you" rail (IPD-2836: "max 3, mixed in with
// the other content"). Wrapped as library items so the compact card renders them.
const forYouChallenges = ref<ExploreLibraryItemDTO[]>([]);
let forYouChallengesRequestId = 0;
const fetchForYouChallenges = async(): Promise<void> => {
  const requestId = ++forYouChallengesRequestId;
  try {
    const recommendations = await api.content.getRecommendedChallenges();
    if (requestId !== forYouChallengesRequestId) {
      return;
    }
    assignIfChanged(forYouChallenges, recommendations.slice(0, 3).map((challenge) => ({
      contentType: LibraryContentTypeDTO.Challenge,
      // The recommended-challenges endpoint carries no topics, and challenges never feed the
      // topic rails anyway (those are educational only).
      topics: [],
      recommended: true,
      challenge,
    })));
  } catch (err) {
    if (requestId === forYouChallengesRequestId) {
      forYouChallenges.value = [];
    }
    logger.error('Failed to fetch the recommended challenges', {err})();
  }
};
// Library calls: one small challenges call, plus one scoped call PER topic
// (topics=X&size=10). The endpoint returns a single ranked list truncated at `size`,
// so one big call cannot guarantee items for every topic — per-topic calls fetch
// exactly what each rail renders. Empty rails hide.
// Rails iterate the fixed enum (order per §2) so the catalog never waits for getTopics.
const TOPIC_RAIL_SIZE = 10;
const CHALLENGE_RAIL_SIZE = 10;
const CATALOG_TOPICS = Object.values(ReccoTopicDTO);
const CATALOG_FACETS = {mediaTypes: [], topics: [], saved: false, recommended: false, partnerIds: []};

/**
 * The partner band's content. Read with `partnerIds` and none of the member's facets, which is why
 * the band only ever renders on the unfiltered view: drawn on a filtered page it would put cards
 * the filter excluded under the page's own "0 results".
 *
 * Empty partners is the "no band" case — a consumer account has no pack.
 */
const fetchPartnerPack = async(): Promise<void> => {
  const requestId = ++partnerPackRequestId;
  const packIds = partners.value.map((partner) => partner.id);
  if (packIds.length === 0) {
    partnerPackItems.value = [];
    partnerPackSettled.value = true;
    return;
  }
  try {
    const {items} = await api.content.getExploreLibrary(
      // Programs are excluded like everywhere else in Explore: they would otherwise put cards in
      // the band that no other section shows.
      {...CATALOG_FACETS, partnerIds: packIds, contentTypes: RENDERABLE_CONTENT_TYPES},
      EXPLORE_LIBRARY_MAX_SIZE,
    );
    if (requestId === partnerPackRequestId) {
      assignIfChanged(partnerPackItems, items);
    }
  } catch (err) {
    // The band hides rather than showing an error: it is a supporting section, not the page.
    if (requestId === partnerPackRequestId) {
      partnerPackItems.value = [];
    }
    logger.error('Failed to fetch the partner pack', {err})();
  } finally {
    if (requestId === partnerPackRequestId) {
      partnerPackSettled.value = true;
    }
  }
};

const fetchPartners = async(): Promise<void> => {
  try {
    const fetched = await api.content.getExplorePartners();
    assignIfChanged(partners, fetched);
  } catch (err) {
    partners.value = [];
    logger.error('Failed to fetch explore partners', {err})();
  }
  await fetchPartnerPack();
};
// One counter per ref rather than one for the whole catalog: the challenges rail can now be
// refreshed on its own, and a full-catalog response that is already stale must not overwrite it
// (nor the other way round). Whoever bumped a counter last is the only writer for that ref.
let catalogRequestId = 0;
let challengeRailRequestId = 0;

// The challenges rail alone, for returns that must not disturb the rest of the page. Safe for the
// restored scroll position: the rail is a horizontal carousel, so changing its contents moves
// nothing vertically — unlike the topic rails, which appear and disappear with their item counts.
const fetchChallengeRail = async(): Promise<void> => {
  const requestId = ++challengeRailRequestId;
  try {
    const {items} = await api.content.getExploreLibrary(
      {...CATALOG_FACETS, contentTypes: [LibraryContentTypeDTO.Challenge]},
      CHALLENGE_RAIL_SIZE,
    );
    if (requestId === challengeRailRequestId) {
      assignIfChanged(
        challengeCatalogItems,
        orderStartedFirst(items.filter((item) => item.contentType === LibraryContentTypeDTO.Challenge)),
      );
    }
  } catch (err) {
    // Keep what is on screen: a failed background refresh must not empty the rail.
    logger.error('Failed to refresh the challenges section', {err})();
  }
};

const fetchLibraryCatalog = async(): Promise<void> => {
  const requestId = ++catalogRequestId;
  const challengeRequestId = ++challengeRailRequestId;
  const emptyFacets = CATALOG_FACETS;
  // Per-rail degradation: one failed call hides ITS rail only, the rest still render.
  const [challengesResult, ...perTopic] = await Promise.allSettled([
    api.content.getExploreLibrary(
      {...emptyFacets, contentTypes: [LibraryContentTypeDTO.Challenge]},
      CHALLENGE_RAIL_SIZE,
    ),
    ...CATALOG_TOPICS.map(async(topic) =>
      api.content.getExploreLibrary(
        {...emptyFacets, topics: [topic], contentTypes: [LibraryContentTypeDTO.Educational]},
        TOPIC_RAIL_SIZE,
      )),
  ]);

  // Guarded per ref: a challenge-only refresh may have overtaken this response.
  if (challengeRequestId === challengeRailRequestId) {
    if (challengesResult.status === 'fulfilled') {
      const challenges = challengesResult.value.items
        .filter((item) => item.contentType === LibraryContentTypeDTO.Challenge);
      assignIfChanged(challengeCatalogItems, orderStartedFirst(challenges));
    } else {
      challengeCatalogItems.value = [];
      const err: unknown = challengesResult.reason;
      logger.error('Failed to fetch the challenges section', {err})();
    }
  }

  if (requestId !== catalogRequestId) {
    return;
  }

  assignIfChanged(topicCatalogRails, CATALOG_TOPICS
    .map((topic, index) => {
      const result = perTopic[index];
      if (result?.status !== 'fulfilled') {
        const err: unknown = result?.reason;
        logger.error('Failed to fetch a topic rail', {topic, err})();
        return {topic, items: []};
      }
      // Re-filter client-side so an over-broad response can't leak items into the wrong rail.
      // On `topics`, the field the BE filtered on — this used to check the singular `topic`,
      // the BE's first match among them, which for content tagged both mental wellbeing and
      // relaxation always resolved to the former and silently emptied that rail (IPD-3032).
      return {
        topic,
        items: result.value.items.filter((item) =>
          item.contentType === LibraryContentTypeDTO.Educational && (item.topics ?? []).includes(topic)),
      };
    })
    .filter((rail) => rail.items.length > 0));
};

// Only drives the filter-bar facet options.
const fetchTopics = async(): Promise<void> => {
  try {
    allTopics.value = await api.content.getTopics();
  } catch (err) {
    allTopics.value = [];
    logger.error('Failed to fetch the topics', {err})();
  }
};

// Only the latest request may write the results (the onActivated silent refresh can
// overlap an in-flight fetch).
let recommendationsRequestId = 0;

// Fetch all recommendations from API. `silent` skips the skeleton (keep-alive refresh).
const fetchRecommendations = async(silent = false): Promise<void> => {
  const requestId = ++recommendationsRequestId;
  if (!silent) {
    loading.value = true;
  }

  try {
    const items = await api.content.getRecommendations() ?? [];
    if (requestId !== recommendationsRequestId) {
      return;
    }
    assignIfChanged(allRecommendations, items);
    // The large spot and the two bands are derived below: which piece takes the spot depends on
    // the partner pack, which arrives from a different request.
    assignIfChanged(topRankedRecommendation, items.find((item) => !item.isFeatured) ?? null);
    areTopElementsReady.value = true;
    // Stamped on success only: a failed load must not buy the staleness gate more quiet time.
    lastLoadedAt = Date.now();
  } catch (err) {
    if (err instanceof AppError && err.statusCode === ErrorCodesDto.NO_ACTIVE_LICENSES) {
      void router.push({path: pagesPath.account.subscription, query: {reason: ErrorCodesDto.NO_ACTIVE_LICENSES}});
      return;
    }
    error.value = 'Failed to fetch recommendations';
    logger.error('Failed to fetch recommendations', {err})();
  } finally {
    loading.value = false;
  }
};

// Initial fetch on component mount
// Check access before mounting
onBeforeMount(() => {
  if (!canAccessExplore.value) {
    // Redirect to paths page if user doesn't have access to explore
    void router.replace(pagesPath.intervention.interventionsList);
  }
});

onMounted(() => {
  void fetchRecommendations();
  void fetchPartners();
  void fetchForYouChallenges();
  void fetchTopics();
  void fetchLibraryCatalog();
  void openAtRequestedAnchor();
  openOnRequestedSaved();
});

onDeactivated(() => {
  anchorWaitGeneration++;
});

onBeforeUnmount(() => {
  anchorWaitGeneration++;
});

let firstActivation = true;
onActivated(() => {
  // The instance survives navigation, so re-check access on every visit (a license can
  // drop the feature mid-session) and count the page view.
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
    return;
  }
  void api.metric.logEvent({
    category: MetricCategory.PreventWebPage,
    action: MetricAction.View,
  });
  if (firstActivation) {
    firstActivation = false;
    return;
  }

  // The instance is kept alive, so a second arrival from the Today tile restores it rather than
  // mounting it: without this the hash would be honoured on the first visit only. Not on a back
  // navigation, which carries the hash it left with and has a scroll position of its own to
  // restore — the one everything else in this hook is careful not to disturb.
  if (!wasPopNavigation()) {
    void openAtRequestedAnchor();
    openOnRequestedSaved();
  }
  // Silent refresh (consumed markers, challenge progress). Skipped only when returning
  // to recent data, so a quick trip into a card and back keeps its rails — and the
  // restored scroll — untouched.
  if (wasPopNavigation() && Date.now() - lastLoadedAt < REFRESH_STALE_AFTER_MS) {
    // Challenge progress moves while the user is inside a challenge, so a returning user would
    // otherwise read the old percentage until the window expired (IPD-3035). Refreshed on its own
    // because it cannot shift the restored scroll; the rest of the catalog still stays put.
    void fetchChallengeRail();
    // The band carries the same challenges, and their progress moves while the user is inside one.
    // Scroll-safe for the same reason the rail is: it is a horizontal carousel.
    void fetchPartnerPack();
    return;
  }
  void fetchRecommendations(true);
  void fetchForYouChallenges();
  void fetchLibraryCatalog();
  // Re-run too: a partners call that failed on mount would otherwise leave the band and the
  // partner chips absent for the whole session, with no way to recover. It fetches the pack itself,
  // so asking for the pack here as well would only send the same size=250 request twice.
  void fetchPartners();
  if (filtersActive.value) {
    void fetchLibrary();
  }
});

</script>

<template>
  <div class="explore-page">
    <div class="explore-header">
      <div class="explore-title-group">
        <h1 class="explore-title">
          {{ $t('recco_explore') }}
        </h1>
        <svg-illustration class="explore-header-arrow" name="right-down-arrow.svg" inner-class="inner-right"/>
      </div>
      <button
        v-if="!loading"
        type="button"
        class="filters-toggle"
        :class="{'filters-toggle--active': filtersOpen || activeCount > 0}"
        :aria-expanded="filtersOpen"
        aria-controls="explore-filter-band"
        @click="filtersOpen = !filtersOpen"
      >
        <svg-illustration class="filters-toggle__icon" name="filters-sliders.svg"/>
        <span>{{ activeCount > 0 ? $t('explore_filters_active', {count: activeCount}) : $t('explore_filters') }}</span>
      </button>
    </div>

    <!-- Skeleton loading state -->
    <div v-if="loading" class="skeleton-wrapper">
      <!-- Featured recommendation skeleton -->
      <div class="skeleton-featured-section">
        <q-skeleton class="skeleton-featured-card"/>
      </div>

      <!-- Recommendations band skeleton -->
      <div class="skeleton-band-section">
        <q-skeleton type="text" width="220px" height="28px" class="skeleton-band-title"/>
        <div class="skeleton-cards-row">
          <q-skeleton v-for="n in 4" :key="n" class="skeleton-card"/>
        </div>
      </div>

      <!-- Topics section skeleton -->
      <div class="skeleton-topics-section">
        <q-skeleton type="text" width="120px" height="28px" class="skeleton-topics-title"/>
        <div class="skeleton-topics-grid">
          <q-skeleton v-for="n in 3" :key="n" class="skeleton-topic-card"/>
        </div>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <explore-filter-bar
        v-if="filtersOpen"
        id="explore-filter-band"
        :class="{'filter-bar-roomy': !(filtersActive && hasFilteredResults)}"
        :media-types="mediaTypes"
        :content-types="contentTypes"
        :topics="selectedTopics"
        :saved="saved"
        :partner-ids="partnerIds"
        :available-topics="availableTopics"
        :partners="partners"
        :is-active="filtersActive"
        @toggle-media-type="toggleMediaType"
        @toggle-content-type="toggleContentType"
        @toggle-topic="toggleTopic"
        @toggle-saved="toggleSaved"
        @toggle-partner="togglePartner"
        @clear-all="clearAll"
      />

      <!-- Filtered view: results re-grouped into the section structure (§2) -->
      <template v-if="filtersActive">
        <template v-if="hasFilteredResults">
          <p class="explore-results-count">
            {{ resultsLabel }}
          </p>
          <explore-library-rail
            v-if="recommendedItems.length > 0"
            :title="$t('recco_for_you')"
            :items="recommendedItems"
            back-label="recco_explore"
          />
          <explore-library-rail
            v-if="featuredItems.length > 0"
            :title="$t('explore_featured_by_org')"
            :items="featuredItems"
            back-label="recco_explore"
          />
          <explore-library-rail
            v-if="challengeItems.length > 0"
            :title="$t('recco_challenges')"
            :items="challengeItems"
            back-label="recco_explore"
            :view-all-to="CHALLENGES_LINK"
            big-challenge-cards
          />
          <explore-library-rail
            v-for="rail in topicRails"
            :key="rail.topic"
            :title="$t(topicLabelKey[rail.topic])"
            :items="rail.items"
            back-label="recco_explore"
            :view-all-to="topicLink(rail.topic)"
          />
          <!-- Educational items whose topic isn't in the topic list — kept visible rather than
               dropped; they belong with the topic rails, so they close that group. -->
          <explore-library-rail
            v-if="otherEducationalItems.length > 0"
            :title="$t('explore_filter_content_type_educational')"
            :items="otherEducationalItems"
            back-label="recco_explore"
          />
        </template>
        <!-- Initial fetch (no results yet): spinner instead of a blank area. Stale results
             stay put during a re-fetch, so this only shows on the first load. -->
        <div v-else-if="libraryLoading" class="explore-loading">
          <q-spinner-dots size="40px" color="primary"/>
        </div>
        <!-- A failed request reads as an error with a retry, not as an empty filter result. -->
        <div v-else-if="libraryError" class="explore-empty">
          <p class="explore-empty__title">
            {{ $t('explore_filter_error_title') }}
          </p>
          <p class="explore-empty__helper">
            {{ $t('explore_filter_error_helper') }}
          </p>
          <button type="button" class="explore-empty__clear" @click="retryLibrary">
            {{ $t('explore_filter_retry') }}
          </button>
        </div>
        <div v-else class="explore-empty">
          <p class="explore-empty__title">
            {{ isSavedOnly ? $t('explore_filter_saved_empty_title') : $t('explore_filter_empty_title') }}
          </p>
          <p v-if="!isSavedOnly" class="explore-empty__helper">
            {{ $t('explore_filter_empty_helper') }}
          </p>
          <button type="button" class="explore-empty__clear" @click="clearAll">
            {{ $t('explore_filter_clear_all') }}
          </button>
        </div>
      </template>

      <!-- Default view: sectioned page (no active filters) -->
      <template v-else>
        <!-- The large spot: the pack's featured piece when there is one, the top recommendation
             otherwise. The eyebrow says which, because they are there for different reasons. -->
        <!-- Held open while the pack is in flight: which piece wins the spot depends on it, and
             letting the rest of the page paint without reserving the space drops the hero in on
             top of it a moment later. Only this section waits — the page no longer does. -->
        <div v-if="!partnerPackSettled" class="skeleton-featured-section">
          <q-skeleton class="skeleton-featured-card"/>
        </div>
        <recco-featured-recommendation
          v-else-if="largeSpot"
          :block-content="largeSpot.content"
          :eyebrow-key="largeSpot.eyebrowKey"
          mark-recommended
        />
        <block-content-band
          v-if="forYouRecommendations.length > 0 || forYouChallenges.length > 0"
          :recommendations="forYouRecommendations"
          :title="$t('recco_for_you')"
          :back-label="'recco_explore'"
          :challenges="forYouChallenges"
          mark-recommended
        />
        <!-- The organisation's pack, minus whatever took the large spot. Hidden entirely — header
             included — when the org has no partner content (§5). Only ever on the unfiltered view:
             it is read by pack alone, so on a filtered page it would show cards the filter
             excluded. -->
        <explore-library-rail
          v-if="partnerBandItems.length > 0"
          :title="$t('explore_featured_by_org')"
          :items="partnerBandItems"
          back-label="recco_explore"
          :view-all-to="PARTNER_CONTENT_LINK"
          always-show-view-all
        />
        <!-- Challenges catalog rail (mockup: includes started challenges with their progress) -->
        <explore-library-rail
          v-if="areTopElementsReady && challengeCatalogItems.length > 0"
          :id="CHALLENGES_ANCHOR"
          :title="$t('recco_challenges')"
          :items="challengeCatalogItems"
          back-label="recco_explore"
          :view-all-to="CHALLENGES_LINK"
          always-show-view-all
          big-challenge-cards
        />
        <!-- Topic catalog rails (replace the old topics navigation grid); "View all"
             is always visible — it is the way into each topic page now. -->
        <template v-if="areTopElementsReady">
          <explore-library-rail
            v-for="rail in topicCatalogRails"
            :key="rail.topic"
            :title="$t(topicLabelKey[rail.topic])"
            :items="rail.items"
            back-label="recco_explore"
            :view-all-to="topicLink(rail.topic)"
            always-show-view-all
          />
        </template>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.explore-page {
  @include page-alignment;
}

.explore-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--Spacing-spacing-2);
  // Breathing room between the title / Filters row and the content (or the band) below.
  padding-bottom: var(--Spacing-spacing-6);
}

.explore-title-group {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  min-width: 0;
}

.explore-title {
  @include font-h1-bold;
  margin: 0;
}

// "Filters" toggle — reveals/hides the filter band. Dark-filled while the band
// is open OR any filter is active, so an active selection stays visible collapsed.
.filters-toggle {
  // Shares the pill skin (border + radius + focus) but sits on the PAGE, not the
  // band, so it uses the ui-20 raised surface (distinct from the ui-10 page in
  // both themes — subtle in light, clearly raised in dark instead of flat/white).
  // Taller 40px trigger, 16px medium label, 8px icon gap.
  @include explore-filter-pill;
  @include font-body;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 40px;
  min-height: 40px;
  background-color: var(--Neutral-UI-neutral-ui-20);
  font-weight: 500;

  &--active {
    @include explore-filter-pill-active;
  }

  &__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

// Space between the collapsible band and the content below it.
.explore-filter-bar {
  margin-bottom: var(--Spacing-spacing-4);
}

// When no "N results" heading follows (no active filter / empty state), give the band a bit
// more breathing room so the content isn't cramped right under it.
.filter-bar-roomy {
  margin-bottom: var(--Spacing-spacing-6);
}

// Match the rails' 48px inter-section gap on this page (the band is shared with
// home/topic, whose own spacing stays untouched).
:deep(.block-content-band) {
  margin-top: var(--Spacing-spacing-7);

  .band-header {
    padding: 0;
  }

  // Trim the track to its content (like the rails): the shared band reserves extra
  // bottom space that reads as widened section gaps on this page.
  // The track bleeds to the window edges; its own gutter insets the first card.
  .content-scroll-container {
    height: $rail-track-height;
    padding: 0;
    margin: 0 calc(-1 * var(--Spacing-spacing-5));

    @include screen-md {
      height: $rail-track-height-md;
    }
  }

  .content-items {
    padding: 6px calc(var(--Spacing-spacing-5) + 4px);
  }
}

:deep(.library-rail) {
  .rail-header {
    padding: 0;
  }

  .rail-scroll {
    padding: 0;
    margin: 0 calc(-1 * var(--Spacing-spacing-5));
  }

  .rail-items {
    padding: 6px calc(var(--Spacing-spacing-5) + 4px);
  }
}

// Result count under the filter band when filters are active. The mockup renders it as a
// SectionHeading (same style as the rail titles), so match font-h3-bold, not a small label.
.explore-results-count {
  @include font-h3-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;

  // Pull the first rail up close to the count (its default section margin-top is too wide here).
  + :deep(.library-rail) {
    margin-top: var(--Spacing-spacing-3);
  }
}

.explore-loading {
  display: flex;
  justify-content: center;
  padding: var(--Spacing-spacing-12) var(--Spacing-spacing-4);
}

.explore-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  padding: var(--Spacing-spacing-12) var(--Spacing-spacing-4);
  text-align: center;

  &__title {
    @include font-h3-bold;
    color: var(--Neutral-UI-neutral-ui-100);
    margin: 0;
  }

  &__helper {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-60);
    margin: 0;
  }

  &__clear {
    @include focus-visible;
    @include font-body-sm-bold;
    margin-top: var(--Spacing-spacing-2);
    padding: var(--Spacing-spacing-2) var(--Spacing-spacing-6);
    border: none;
    border-radius: var(--button-border-radius);
    background-color: var(--Neutral-UI-neutral-ui-100);
    color: var(--Neutral-UI-neutral-ui-0);
    cursor: pointer;
  }
}

// Skeleton styles
.skeleton-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
}

.skeleton-featured-section {
  margin-bottom: var(--Spacing-spacing-4);
}

.skeleton-featured-card {
  width: 100%;
  height: 250px;
  border-radius: var(--card-border-radius);

  @include screen-md {
    height: 450px;
  }

  @include screen-lg {
    height: 450px;
  }
}

.skeleton-band-section {
  margin-bottom: var(--Spacing-spacing-4);
}

.skeleton-band-title {
  margin-bottom: var(--Spacing-spacing-4);
}

.skeleton-cards-row {
  display: flex;
  gap: var(--Spacing-spacing-4);
  overflow: hidden;
}

.skeleton-card {
  flex-shrink: 0;
  width: 280px;
  height: 320px;
  border-radius: var(--card-border-radius);

  @include screen-md {
    width: 300px;
    height: 360px;
  }
}

.skeleton-topics-section {
  margin-bottom: var(--Spacing-spacing-8);
}

.skeleton-topics-title {
  margin-bottom: var(--Spacing-spacing-2);
}

.skeleton-topics-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: var(--Spacing-spacing-4);

  @include screen-sm {
    grid-template-columns: repeat(2, 1fr);
  }

  @include screen-lg {
    grid-template-columns: repeat(3, 1fr);
  }
}

.skeleton-topic-card {
  height: 200px;
  border-radius: var(--card-border-radius);

  @include screen-md {
    height: 240px;
  }
}
</style>
